'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { DiscoveryPageLayout, SitePageLayout } from '@/platform/layout';
import { BlogFeedCard } from '@/platform/blog/BlogFeedCard';
import { brandConfig } from '@/config/brand';
import { listBlogPosts, type BlogPost } from '@/platform/blog/api';

const PAGE_SIZE = 20;

export function BlogFeedPage({
  tourMode = false,
  initialPosts,
  postHrefPrefix,
  authorFixtures,
  onSelectAuthor,
}: {
  tourMode?: boolean;
  initialPosts?: BlogPost[];
  /** e.g. /tour/blog/ */
  postHrefPrefix?: string;
  authorFixtures?: Record<string, { displayName?: string; handle?: string; avatarUrl?: string }>;
  onSelectAuthor?: (userId: string) => void;
}) {
  const [posts, setPosts] = useState<BlogPost[]>(initialPosts ?? []);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(!tourMode);
  const [loadingMore, setLoadingMore] = useState(false);
  const inFlightRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const Page = tourMode ? SitePageLayout : DiscoveryPageLayout;

  const loadPage = useCallback(
    async (cursor?: string) => {
      if (tourMode) return;
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      const isInitial = !cursor;
      if (isInitial) setLoading(true);
      else setLoadingMore(true);
      try {
        const data = await listBlogPosts({ limit: PAGE_SIZE, cursor });
        const batch = data.posts ?? [];
        setPosts((prev) => (isInitial ? batch : [...prev, ...batch]));
        setNextCursor(data.nextCursor);
      } catch {
        if (isInitial) setPosts([]);
        setNextCursor(undefined);
      } finally {
        inFlightRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [tourMode],
  );

  useEffect(() => {
    if (tourMode) {
      setPosts(initialPosts ?? []);
      setLoading(false);
      return;
    }
    void loadPage();
  }, [loadPage, tourMode, initialPosts]);

  useEffect(() => {
    if (tourMode) return;
    const el = sentinelRef.current;
    if (!el || !nextCursor || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          void loadPage(nextCursor);
        }
      },
      { rootMargin: '240px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [nextCursor, loading, loadingMore, loadPage, tourMode]);

  return (
    <Page
      title={brandConfig.blog.label}
      description={brandConfig.blog.description}
      actions={
        tourMode ? undefined : (
          <Link href="/blog/compose/" className="btn btn-primary">
            Write a post
          </Link>
        )
      }
    >
      {loading ? (
        <p className="text-muted">Loading posts…</p>
      ) : posts.length === 0 ? (
        <p className="text-muted">No blog posts yet.</p>
      ) : (
        <div className="social-feed" role="list" aria-busy={loadingMore}>
          {posts.map((p) => (
            <div key={p.id} role="listitem">
              <BlogFeedCard
                post={p}
                hrefOverride={postHrefPrefix ? `${postHrefPrefix}${p.slug}/` : undefined}
                disableNavigation={tourMode && !postHrefPrefix}
                authorFixture={authorFixtures?.[p.authorId]}
                onSelectAuthor={onSelectAuthor}
              />
            </div>
          ))}
          {nextCursor && <div ref={sentinelRef} className="social-feed__sentinel" aria-hidden />}
          {loadingMore && (
            <p className="social-feed__status text-muted" role="status">
              Loading more…
            </p>
          )}
        </div>
      )}
    </Page>
  );
}
